import type {
  FormattingRule,
  ComponentOrReferenceFormattingRule,
} from '@ardoq/data-model';
import {
  type Formatting,
  type FormattingGroup,
  getCustomFieldsFormattingGroupIfAnyFieldIsAvailableOrNull,
  isComponentOrReferenceFormattingRule,
} from '@ardoq/perspectives';
import {
  APIEntityType,
  type APIFieldAttributes,
  type FieldGlobalAttributes,
} from '@ardoq/api-types';
import { dateRangeOperations } from '@ardoq/date-range';
import { fieldOps } from 'models/utils/fieldOps';
import { isString } from 'lodash';
import { Locale } from '@ardoq/locale';

const getCanonicalRuleFieldName = (field: string): string | null => {
  if (!field) return null;
  return dateRangeOperations.isCombinedDateRangeFieldNames(field)
    ? dateRangeOperations.toStartDateName(
        dateRangeOperations.extractDateRangeFieldName(field)
      )
    : field;
};

export const resolveRuleFieldData = (
  field: string,
  globalFieldsByName: Record<string, FieldGlobalAttributes>,
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>
): FieldGlobalAttributes | null => {
  const fieldName = getCanonicalRuleFieldName(field);
  if (!fieldName) return null;
  const globalField = globalFieldsByName[fieldName];
  if (globalField) return globalField;
  // globalFields$ pairs date-range constituents into a single combined entry, so
  // a lookup by the start-date constituent misses. Fall back to the constituent
  // map (pre-filtered upstream to drop template-model fields, matching the
  // semantics of the previous fieldInterface.getGlobalFieldByName path).
  if (dateRangeOperations.fieldNameIsPartOfDateRangeField(fieldName)) {
    const apiField = nonTemplateFieldsByName.get(fieldName);
    return apiField ? fieldOps.toGlobalField(apiField) : null;
  }
  return null;
};

export const buildFieldDataByRuleId = (
  rules: FormattingRule[],
  globalFieldsByName: Record<string, FieldGlobalAttributes>,
  nonTemplateFieldsByName: Map<string, APIFieldAttributes>
): Record<string, FieldGlobalAttributes | null> => {
  const result: Record<string, FieldGlobalAttributes | null> = {};
  for (const rule of rules) {
    if (!isComponentOrReferenceFormattingRule(rule)) continue;
    if (!rule.field) continue;
    result[rule.__id] = resolveRuleFieldData(
      rule.field,
      globalFieldsByName,
      nonTemplateFieldsByName
    );
  }
  return result;
};

export const getCustomFieldFormatting = (
  rule: ComponentOrReferenceFormattingRule,
  fieldData: FieldGlobalAttributes | null,
  locale: Locale
): Formatting | undefined => {
  if (!rule.field || !fieldData) return;

  const formattingGroup =
    getCustomFieldsFormattingGroupIfAnyFieldIsAvailableOrNull(
      [fieldData],
      false,
      locale
    );

  // For the combined date range field, we have two formatting options.
  // The first one is for the start date, which we don't need.
  const formatting = formattingGroup?.filters.find(f => f.id === rule.field);

  if (formatting) {
    formatting.isDisabled = true;
    return formatting;
  }
};

type GetInvalidRuleFilterFunctionArgs = {
  conditionalFormattingRules: FormattingRule[];
  componentsFormattingGroups: FormattingGroup[];
  referencesFormattingGroups: FormattingGroup[];
  fieldDataByRuleId: Record<string, FieldGlobalAttributes | null>;
  locale: Locale;
};
export const getFormattingForInvalidRuleByRuleId = ({
  conditionalFormattingRules,
  componentsFormattingGroups,
  referencesFormattingGroups,
  fieldDataByRuleId,
  locale,
}: GetInvalidRuleFilterFunctionArgs) => {
  const formattingForInvalidRuleByRuleId: Record<string, Formatting> = {};

  conditionalFormattingRules.forEach(rule => {
    if (
      rule.type === APIEntityType.COMPONENT ||
      rule.type === APIEntityType.REFERENCE
    ) {
      const isComponent = rule.type === APIEntityType.COMPONENT;

      const formattingGroups = isComponent
        ? componentsFormattingGroups
        : referencesFormattingGroups;

      const existingFormatting = formattingGroups
        .flatMap(({ filters }) => filters)
        .find(group => group.id === rule.field);

      if (!existingFormatting) {
        const formatting = getCustomFieldFormatting(
          rule,
          fieldDataByRuleId[rule.__id] ?? null,
          locale
        );
        if (formatting) {
          formattingForInvalidRuleByRuleId[rule.__id] = formatting;
        }

        return;
      }

      if (rule.field === 'type') {
        const typeFilter = formattingGroups[0].filters.find(
          filter => filter.id === 'type'
        );

        const hasFormattingForRuleValue = typeFilter?.selectOptions?.some(
          option => option.value === rule.value
        );

        if (!typeFilter || !isString(rule.value) || hasFormattingForRuleValue) {
          return;
        }

        formattingForInvalidRuleByRuleId[rule.__id] = {
          ...typeFilter,
          selectOptions: [
            {
              label: rule.value,
              value: rule.value,
              isDisabled: true,
            },
            ...(typeFilter.selectOptions || []),
          ],
        };
      }
    }
  });

  return formattingForInvalidRuleByRuleId;
};
